import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import { Avatar, Box, Button, ButtonBase, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { ChineseTeacher } from '../../../api/UserApi';
import female_avatar_1 from '../../../asset/female_avatar_1.png';
import female_avatar_2 from '../../../asset/female_avatar_2.png';
import male_avatar from '../../../asset/male_avatar.png';
import AppBarContext from '../../../context/AppBarContext';
import { isSettingsComplete } from '../../../page/Lesson';
import AudioPlayButton from '../components/AudioPlayButton';
import PhoneFrame from '../components/PhoneFrame';
import { completedLessonCount, mockCourses } from '../mockData';
import { normalizeStudentAvatarColor, studentAvatarColorNames, studentAvatarColors } from '../studentAvatarColors';
import { BLUE, blueButtonSx, cardSx, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT, YELLOW, yellowButtonSx } from '../studentStyles';

const teacherOptions: { value: ChineseTeacher; src: string; label: string; caption: string }[] = [
    { value: ChineseTeacher.Male, src: male_avatar, label: 'Male teacher voice', caption: 'Male' },
    { value: ChineseTeacher.Female, src: female_avatar_1, label: 'Female teacher voice', caption: 'Female A' },
    { value: ChineseTeacher.Female2, src: female_avatar_2, label: 'Second female teacher voice', caption: 'Female B' },
];

const audioSpeedOptions = [
    { value: 'slow', label: 'Slower', hint: '0.8×' },
    { value: 'normal', label: 'Normal', hint: '1.0×' },
    { value: 'fast', label: 'Faster', hint: '1.2×' },
];

const scoringOptions = [
    { value: 'relaxed', label: 'Relaxed' },
    { value: 'standard', label: 'Standard' },
    { value: 'strict', label: 'Strict' },
];

// The "Learn Characters" prompt (DialogPromptLearnWithCharacterModel), asked as
// a setting here instead of mid-lesson.
const characterOptions = [
    { value: 'characters', label: 'Characters', hint: '汉字 + pinyin' },
    { value: 'pinyin', label: 'Pinyin only', hint: 'no 汉字' },
];

const overlineSx = { fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE };
const fieldLabelSx = { fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE };

function StatTile({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
    return (
        <Box sx={{ flex: 1, p: 1.5, borderRadius: '14px', backgroundColor: SURFACE_SOFT, textAlign: 'center' }}>
            {icon}
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 0.25 }}>
                {value}
            </Typography>
            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                {label}
            </Typography>
        </Box>
    );
}

function OptionPill({ label, hint, selected, onClick }: { label: string; hint?: string; selected: boolean; onClick: () => void }) {
    return (
        <ButtonBase
            onClick={onClick}
            sx={{
                flex: 1,
                flexDirection: 'column',
                px: 1.5,
                py: 1,
                borderRadius: '14px',
                border: `2px solid ${selected ? BLUE : RING_TRACK}`,
                backgroundColor: selected ? `${BLUE}14` : '#fff',
                transition: 'background-color 150ms ease, border-color 150ms ease',
            }}
        >
            <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: selected ? BLUE : SLATE }}>
                {label}
            </Typography>
            {hint && (
                <Typography sx={{ fontFamily: INTER, fontSize: 11, color: selected ? BLUE : SLATE, opacity: 0.8 }}>
                    {hint}
                </Typography>
            )}
        </ButtonBase>
    );
}

export default function StudentProfile() {
    const history = useHistory();
    const { setIsLoggedIn } = React.useContext(AppBarContext);

    const settingsComplete = isSettingsComplete();

    const [name, setName] = React.useState(localStorage.getItem('studentName') || '');
    const [avatarColor, setAvatarColor] = React.useState(() => normalizeStudentAvatarColor(localStorage.getItem('studentAvatarColor')));
    const [teacher, setTeacher] = React.useState<ChineseTeacher | null>(
        (localStorage.getItem('teacher') as ChineseTeacher | null));
    const [audioSpeed, setAudioSpeed] = React.useState(localStorage.getItem('studentAudioSpeed') || 'normal');
    const [scoring, setScoring] = React.useState(localStorage.getItem('studentScoring') || 'standard');
    const [characters, setCharacters] = React.useState(localStorage.getItem('studentCharacters') || 'characters');

    const lessonsDone = mockCourses.reduce((sum, course) => sum + completedLessonCount(course), 0);
    const exercisesDone = mockCourses.reduce(
        (sum, course) => sum + course.lessons.reduce((lessonSum, lesson) => lessonSum + lesson.completedPrompts, 0), 0);

    const handleSave = () => {
        // Prototype: persist to localStorage only — a real implementation would call UserApi.update()
        localStorage.setItem('studentName', name.trim());
        localStorage.setItem('studentAvatarColor', avatarColor);
        localStorage.setItem('studentAudioSpeed', audioSpeed);
        localStorage.setItem('studentScoring', scoring);
        localStorage.setItem('studentCharacters', characters);
        if (teacher) {
            localStorage.setItem('teacher', teacher);
        }
        if (!localStorage.getItem('studentAvatar')) {
            // Placeholder so isSettingsComplete() can pass; the real app sets this via the avatar exercise
            localStorage.setItem('studentAvatar', 'initial');
        }
        history.push('/mockup/student');
    };

    const handleLogout = () => {
        localStorage.removeItem('jwt');
        setIsLoggedIn(false);
        history.push('/mockup/student');
    };

    return (
        <PhoneFrame title="Profile" onBack={settingsComplete ? () => history.push('/mockup/student') : undefined}>
            <Stack spacing={3}>

                {settingsComplete ? (
                    /* Profile header: who you are + progress at a glance */
                    <Box sx={{ ...cardSx, p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ width: 64, height: 64, bgcolor: avatarColor, fontFamily: POPPINS, fontWeight: 600, fontSize: 26 }}>
                                {(name || '?').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography noWrap sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY }}>
                                    {name || 'New student'}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                    Learning Chinese — keep it up!
                                </Typography>
                            </Box>
                        </Box>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                            <StatTile
                                icon={<MenuBookRoundedIcon sx={{ color: BLUE, fontSize: 20 }} />}
                                value={`${lessonsDone}`}
                                label="lessons done"
                            />
                            <StatTile
                                icon={<CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 20 }} />}
                                value={`${exercisesDone}`}
                                label="exercises"
                            />
                        </Stack>
                    </Box>
                ) : (
                    <Box sx={{ backgroundColor: `${YELLOW}33`, borderRadius: '16px', p: 2 }}>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY }}>
                            Let&apos;s finish setting you up
                        </Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5 }}>
                            Pick your name and teacher voice to start learning.
                        </Typography>
                    </Box>
                )}

                {/* About you */}
                <Box sx={{ ...cardSx, p: 3 }}>
                    <Typography sx={overlineSx}>ABOUT YOU</Typography>
                    <TextField
                        fullWidth
                        label="Your name"
                        value={name}
                        onChange={event => setName(event.target.value)}
                        sx={{ mt: 2 }}
                    />
                    <Typography sx={{ ...fieldLabelSx, mt: 2.5, mb: 1.25 }}>
                        Avatar color
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        {studentAvatarColors.map(color => {
                            const selected = avatarColor === color;
                            return (
                                <ButtonBase
                                    key={color}
                                    aria-label={`${studentAvatarColorNames[color]} avatar color`}
                                    onClick={() => setAvatarColor(color)}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '50%',
                                        backgroundColor: color,
                                        border: '3px solid #fff',
                                        boxShadow: selected ? `0 0 0 2px ${NAVY}` : '0 2px 8px rgba(0,0,0,0.12)',
                                    }}
                                >
                                    {selected && <CheckRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />}
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                {/* Lesson settings */}
                <Box sx={{ ...cardSx, p: 3 }}>
                    <Typography sx={overlineSx}>LESSON SETTINGS</Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <Typography sx={{ ...fieldLabelSx, flex: 1 }}>
                            Teacher voice
                        </Typography>
                        <AudioPlayButton ariaLabel="Preview teacher voice" size={32} />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 1.5 }}>
                        {teacherOptions.map(option => (
                            <Box key={option.value} sx={{ textAlign: 'center' }}>
                                <Avatar
                                    src={option.src}
                                    alt={option.label}
                                    onClick={() => setTeacher(option.value)}
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        cursor: 'pointer',
                                        border: teacher === option.value ? `3px solid ${BLUE}` : '3px solid transparent',
                                    }}
                                />
                                <Typography sx={{
                                    fontFamily: INTER,
                                    fontWeight: teacher === option.value ? 700 : 400,
                                    fontSize: 11,
                                    color: teacher === option.value ? BLUE : SLATE,
                                    mt: 0.5,
                                }}>
                                    {option.caption}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={{ ...fieldLabelSx, mt: 3, mb: 1 }}>
                        Audio speed
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {audioSpeedOptions.map(option => (
                            <OptionPill
                                key={option.value}
                                label={option.label}
                                hint={option.hint}
                                selected={audioSpeed === option.value}
                                onClick={() => setAudioSpeed(option.value)}
                            />
                        ))}
                    </Box>

                    <Typography sx={{ ...fieldLabelSx, mt: 3, mb: 1 }}>
                        Reading
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {characterOptions.map(option => (
                            <OptionPill
                                key={option.value}
                                label={option.label}
                                hint={option.hint}
                                selected={characters === option.value}
                                onClick={() => setCharacters(option.value)}
                            />
                        ))}
                    </Box>

                    <Typography sx={{ ...fieldLabelSx, mt: 3, mb: 1 }}>
                        Pronunciation grading
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {scoringOptions.map(option => (
                            <OptionPill
                                key={option.value}
                                label={option.label}
                                selected={scoring === option.value}
                                onClick={() => setScoring(option.value)}
                            />
                        ))}
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 1 }}>
                        How strict the speaking feedback is. You can change this anytime.
                    </Typography>
                </Box>

                <Button
                    fullWidth
                    variant="contained"
                    disabled={name.trim().length === 0 || teacher == null}
                    onClick={handleSave}
                    sx={{ ...(settingsComplete ? blueButtonSx : yellowButtonSx), py: 1.5 }}
                >
                    {settingsComplete ? 'Save changes' : 'Start learning'}
                </Button>

                <Button
                    startIcon={<LogoutRoundedIcon />}
                    onClick={handleLogout}
                    sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 14, color: SLATE, alignSelf: 'center' }}
                >
                    Log out
                </Button>

            </Stack>
        </PhoneFrame>
    );
}
